import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeftIcon,
  PaperAirplaneIcon,
} from "@heroicons/react/24/outline";
import { useAuthContext } from "@/lib/auth-context";
import LoadingSpinner from "../../../components/LoadingSpinner";

const CORE_API = import.meta.env.VITE_CORE_API_URL || '';

type GuardianMessage = {
  id: string;
  body: string;
  created_at: string;
  sender_role: string;
};

type GuardianThread = {
  thread_id?: string;
  ticket_id?: string;
  student_id?: string;
  student_name: string;
  teacher_id?: string;
  teacher_name: string;
  messages?: GuardianMessage[];
};

export default function GuardianMessageThread() {
  const { user, session } = useAuthContext() as any;
  const { threadId } = useParams<{ threadId: string }>();
  const [searchParams] = useSearchParams();

  const teacherId = searchParams.get("teacher") || undefined;
  const studentId = searchParams.get("student") || undefined;

  const [thread, setThread] = useState<GuardianThread | null>(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const userId =
    user?.id ||
    (typeof localStorage !== "undefined" && localStorage.getItem("dev_user_id")) ||
    import.meta.env.VITE_DEV_USER_ID ||
    "";
  const accessToken = session?.access_token;

  // auto-scroll
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(scrollToBottom, [thread?.messages?.length]);

  useEffect(() => {
    let isMounted = true;

    const fetchThread = async (idToLoad: string) => {
      setLoading(true);
      setError(null);

      try {
        const res = await fetch(`${CORE_API}/api/v1/messages/threads/${encodeURIComponent(idToLoad)}`, {
          headers: {
            accept: "application/json",
            "x-user-id": userId,
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (!res.ok) {
          const body = await res.text();
          throw new Error(body || `Failed with status ${res.status}`);
        }

        const raw = await res.json();
        const data = (raw.data ?? raw) as {
          thread: GuardianThread;
          messages?: GuardianMessage[];
        };

        if (isMounted) {
          const normalizedId =
            data.thread.thread_id || data.thread.ticket_id || idToLoad;

          setThread({
            ...data.thread,
            thread_id: normalizedId,
            ticket_id: data.thread.ticket_id || normalizedId,
            messages: data.messages || data.thread.messages || [],
          });
        }
      } catch (err) {
        console.error(err);
        if (isMounted) {
          setError("Unable to load this conversation.");
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    const loadThread = async () => {
      // Wait until we have an authenticated user before attempting the request to avoid 401s.
      if (!userId || !accessToken) {
        return;
      }

      if (threadId) {
        await fetchThread(threadId);
        return;
      }

      if (!studentId || !teacherId) {
        setError("Missing student or teacher information for new conversation.");
        setLoading(false);
        return;
      }

      // No existing thread: stay idle until the first message send, but clear loading.
      setLoading(false);
    };

    loadThread();

    return () => {
      isMounted = false;
    };
  }, [threadId, studentId, teacherId, userId, accessToken]);

  const handleSend = async () => {
    if (!draft.trim()) return;
    if (!userId || !accessToken) {
      setError("Unable to send your message without an active session.");
      return;
    }
    if (!thread && (!studentId || !teacherId)) {
      setError("Missing student/teacher info for starting this conversation.");
      return;
    }

    setSending(true);
    setError(null);

    try {
      const payload: Record<string, unknown> = {
        body: draft.trim(),
      };

      // Existing thread or newly fetched one
      if (thread?.thread_id) {
        payload.thread_id = thread.thread_id;
      } else {
        if (studentId) payload.student_id = studentId;
        if (teacherId) payload.teacher_id = teacherId;
      }

      const res = await fetch(`${CORE_API}/api/v1/messages/send`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          accept: "application/json",
          "x-user-id": userId,
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const body = await res.text();
        throw new Error(body || `Failed with status ${res.status}`);
      }

      const raw = await res.json();
      const data = (raw.data ?? raw) as {
        thread?: GuardianThread;
        thread_id?: string;
        message?: GuardianMessage;
      };

      const responseThreadId =
        data.thread?.thread_id || data.thread?.ticket_id || data.thread_id;

      if (data.thread) {
        setThread({
          ...data.thread,
          thread_id: responseThreadId || thread?.thread_id,
          ticket_id: data.thread.ticket_id || responseThreadId,
          messages: data.thread.messages || [],
        });
      } else if (responseThreadId) {
        // Update locally with the returned message while preserving existing thread state.
        if (data.message && thread?.messages) {
          const newMessage: GuardianMessage = {
            id: data.message.id,
            body: data.message.body,
            created_at: data.message.created_at,
            sender_role: data.message.sender_role,
          };

          setThread({
            ...(thread || {
              student_name: "Student",
              teacher_name: "Teacher",
            }),
            thread_id: responseThreadId,
            ticket_id: thread?.ticket_id || responseThreadId,
            messages: [...(thread?.messages || []), newMessage],
          });
        } else {
          // If we don't have enough context, refetch the thread to stay in sync.
          const refreshed = await fetch(
            `${CORE_API}/api/v1/messages/threads/${encodeURIComponent(responseThreadId)}`,
            {
              headers: {
                accept: "application/json",
                "x-user-id": userId,
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );

          if (refreshed.ok) {
            const refreshedRaw = await refreshed.json();
            const refreshedData = (refreshedRaw.data ?? refreshedRaw) as {
              thread: GuardianThread;
              messages?: GuardianMessage[];
            };

            setThread({
              ...refreshedData.thread,
              thread_id: responseThreadId,
              ticket_id: refreshedData.thread.ticket_id || responseThreadId,
              messages:
                refreshedData.messages || refreshedData.thread.messages || [],
            });
          }
        }
      }

      setDraft("");
      // scrollToBottom will run via useEffect on messages change
    } catch (err) {
      console.error(err);
      setError("Unable to send your message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = (
    e
  ) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!sending) {
        handleSend();
      }
    }
  };

  if (loading) {
    return (
      <div className="w-full flex justify-center mt-20">
        <LoadingSpinner />
      </div>
    );
  }

  if (error && !thread) {
    return (
      <div className="max-w-xl mx-auto mt-16 px-4 text-center">
        <Link
          to="/portal/guardian/messages"
          className="inline-flex items-center text-sm text-blue-600 hover:text-blue-500 mb-3"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to messages
        </Link>
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  const titleStudent = thread?.student_name || "Student";
  const titleTeacher = thread?.teacher_name || "Teacher";

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b bg-white">
        <Link
          to="/portal/guardian/messages"
          className="inline-flex items-center text-sm text-blue-600 hover:text-blue-500"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-1" />
          Back
        </Link>
        <div className="flex-1">
          <p className="text-sm font-semibold text-gray-900">
            {titleTeacher}
          </p>
          <p className="text-xs text-gray-500">
            About {titleStudent}
          </p>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto bg-gray-50 px-3 py-4 space-y-3">
        {(thread?.messages || []).map((msg) => {
          const isGuardian = msg.sender_role === "guardian";
          const isTeacher = msg.sender_role === "teacher";
          const isSystemOrAdmin =
            msg.sender_role === "system" || msg.sender_role === "admin";

          if (isSystemOrAdmin) {
            return (
              <div
                key={msg.id}
                className="flex justify-center text-xs text-gray-400"
              >
                <span className="px-3 py-1 rounded-full bg-gray-100">
                  {msg.body}
                </span>
              </div>
            );
          }

          return (
            <div
              key={msg.id}
              className={`flex ${
                isGuardian ? "justify-end" : "justify-start"
              }`}
            >
              <div
                className={`max-w-xs rounded-2xl px-3 py-2 text-sm shadow-sm ${
                  isGuardian
                    ? "bg-blue-600 text-white rounded-br-sm"
                    : "bg-white text-gray-900 rounded-bl-sm border border-gray-200"
                }`}
              >
                <p className="whitespace-pre-wrap">{msg.body}</p>
                <p
                  className={`mt-1 text-[10px] ${
                    isGuardian ? "text-blue-100" : "text-gray-400"
                  } text-right`}
                >
                  {new Date(msg.created_at).toLocaleString()}
                  {isTeacher && " · Teacher"}
                </p>
              </div>
            </div>
          );
        })}

        <div ref={messagesEndRef} />
      </div>

      {/* Error inline */}
      {error && (
        <div className="px-4 py-2 bg-red-50 text-xs text-red-600">
          {error}
        </div>
      )}

      {/* Composer */}
      <div className="border-t bg-white px-3 py-2">
        <div className="flex items-end gap-2">
          <textarea
            className="flex-1 resize-none rounded-2xl border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            rows={2}
            placeholder="Type your message…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={sending || !draft.trim()}
            className="inline-flex items-center justify-center rounded-full bg-blue-600 p-3 text-white shadow-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-500"
          >
            <PaperAirplaneIcon className="h-5 w-5" />
          </button>
        </div>
        <p className="mt-1 text-[11px] text-gray-400">
          Press Enter to send · Shift+Enter for a new line
        </p>
      </div>
    </div>
  );
}
